import { db } from '@core/db/db'
import { EventBus } from '@core/bus/EventBus'
import { PluginRegistry } from '@core/plugins/registry'
import { BackupService } from '@core/services/backup/BackupService'
import { WebBackupStorage } from '@core/services/backup/WebBackupStorage'
import { NativeBackupStorage } from '@core/services/backup/NativeBackupStorage'
import type { BackupStorage } from '@core/services/backup/BackupStorage'
import { ScannerService } from '@core/services/scanner/ScannerService'
import { Capacitor } from '@capacitor/core'
import { CatalogService } from '@core/services/catalog/CatalogService'
import { AssetService } from '@core/services/assets/AssetService'
import { SaleService } from '@core/services/sale/SaleService'
import { fiscalService } from '@core/services/fiscal/FiscalService'
import { fiscalQueueWorker } from '@core/services/fiscal/FiscalQueueWorker'
import { cloudFiscalizationWorker } from '@core/services/fiscal/CloudFiscalizationWorker'
import { syncService } from '@core/services/sync/SyncService'
import { scalesService } from '@core/services/scales/ScalesService'
import { ShiftService } from '@core/services/shift/ShiftService'
import { correctionService } from '@core/services/correction/CorrectionService'
import { createCloudGateway, type CloudGateway } from '@core/cloud/gateway'
import type { MarkingProvider } from '@core/marking/types'
import { ModuleUiRegistry } from '@core/modules/registry'
import { syncAllModuleStates } from '@core/modules/enable'
import { PaymentRegistry } from '@core/payments/registry'
import { ruCzPlugin } from '@plugins/ru-cz'
import { czModuleUi } from '@plugins/ru-cz/ui'
import { mercuryModuleUi } from '@plugins/ru-mercury/ui'
import { ruMercuryPlugin, type RuMercuryPlugin } from '@plugins/ru-mercury'
import { ruSbpPlugin } from '@plugins/ru-sbp'
import { createUzPayProvider } from '@plugins/uz-pay/provider'
import { uzCrptPlugin } from '@plugins/uz-crpt'
import { META_STORE_CURRENCY } from '@core/db/schema'
import type { CurrencyCode } from '@core/utils/currency'

/** Юрисдикция магазина — определяет набор регуляторных плагинов. */
export type Jurisdiction = 'RU' | 'UZ'

/** Сопоставление валюты магазина с юрисдикцией. */
function jurisdictionFromCurrency(currency: CurrencyCode): Jurisdiction {
  return currency === 'UZS' ? 'UZ' : 'RU'
}

/** Собранное ядро приложения — результат bootstrap. */
export interface AppCore {
  bus: EventBus
  /** Шлюз к облаку: общие настройки/состояние модулей. */
  cloud: CloudGateway
  registry: PluginRegistry
  backup: BackupService
  scanner: ScannerService
  catalog: CatalogService
  /** Локальный кэш загруженных картинок категорий и товаров. */
  assets: AssetService
  sale: SaleService
  /** Управление кассовыми сменами (open/close, Z-отчёт). */
  shift: ShiftService
  /**
   * Модуль маркировки активной юрисдикции (`null` — маркировка не регулируется).
   * Ядро получает только порт, без знания о ЧЗ/Asl Belgisi.
   */
  marking: MarkingProvider | null
  /** Плагин Меркурий — для админки ФГИС Меркурий. */
  mercury: RuMercuryPlugin
  /**
   * Реестр платёжных провайдеров юрисдикции. Ядро работает только через порт:
   * `providerFor(method).createQr(...)` и общий UI-резолвер.
   */
  payments: PaymentRegistry
  /** Карточки модулей для админки (экраны живут в самих модулях). */
  moduleUi: ModuleUiRegistry
  /** Сервис фискализации (локальный кассовый агент, ФЗ-54). */
  fiscal: typeof fiscalService
  /** Сервис электронных весов (тот же локальный агент, WS). */
  scales: typeof scalesService
  /** Сервис чеков коррекции (ФЗ-54, ФФД). */
  correction: typeof correctionService
  status: BootStatus
}

/** Статусы инфраструктуры для UI-индикаторов. */
export interface BootStatus {
  dbReady: boolean
  restored: boolean
  pluginCount: number
  pluginIds: string[]
  backupScheduled: boolean
  scannerListening: boolean
  /** Имя активной версии каталога (для статус-индикаторов). */
  activeCatalogVersion: string | null
  /** Активная юрисдикция магазина (RU/UZ) — какой набор плагинов загружен. */
  jurisdiction: Jurisdiction
  /** Валюта магазина. */
  currency: CurrencyCode
}

/**
 * Инициализация ядра («скелет с дымом»).
 *
 * Порядок: открыть БД -> восстановить из бэкапа при необходимости ->
 * создать шину -> зарегистрировать и инициализировать плагины ->
 * запустить авто-бэкап и слушатель сканера.
 *
 * Плагины регистрируются ЗДЕСЬ (а не в Ядре), сохраняя однонаправленную
 * изоляцию. Для СНГ достаточно подменить этот список по стране.
 */
export async function bootstrap(): Promise<AppCore> {
  const status: BootStatus = {
    dbReady: false,
    restored: false,
    pluginCount: 0,
    pluginIds: [],
    backupScheduled: false,
    scannerListening: false,
    activeCatalogVersion: null,
    jurisdiction: 'RU',
    currency: 'RUB'
  }

  // 1. БД.
  await db.open()
  status.dbReady = true

  // 2. Отказоустойчивость: восстановление из последнего снимка при пустой БД.
  //    На нативной платформе (Capacitor iOS/Android) пишем в защищённую ФС
  //    через Filesystem; в браузере — web-фолбэк (store backups в Dexie).
  const backupStorage: BackupStorage = Capacitor.isNativePlatform()
    ? new NativeBackupStorage()
    : new WebBackupStorage(db)
  const backup = new BackupService(db, backupStorage)
  status.restored = await backup.restoreIfNeeded()

  // 3. Шина событий и шлюз к облаку (токен/адрес остаются в ядре).
  const bus = new EventBus()
  const cloud = createCloudGateway()
  // Прокидываем шину в фоновый синхронизатор — для эмита CATALOG_UPDATED
  // при получении облачной дельты каталога (мгновенное обновление кассы).
  syncService.setEventBus(bus)

  // 4. Динамический выбор набора плагинов по юрисдикции магазина.
  //    Юрисдикция определяется валютой (store_currency в meta): UZS → UZ,
  //    иначе → RU (по умолчанию, в т.ч. при отсутствии настройки).
  //    Так узбекские и российские регуляторные требования не конфликтуют.
  const currencyEntry = await db.meta.get(META_STORE_CURRENCY)
  const currency: CurrencyCode = currencyEntry?.value === 'UZS' ? 'UZS' : 'RUB'
  const jurisdiction = jurisdictionFromCurrency(currency)
  status.currency = currency
  status.jurisdiction = jurisdiction

  const registry = new PluginRegistry()
  const payments = new PaymentRegistry()
  const moduleUi = new ModuleUiRegistry()
  if (jurisdiction === 'UZ') {
    // Узбекистан: национальная маркировка Asl Belgisi + Click/Payme.
    registry.register(uzCrptPlugin)
    payments.register(
      createUzPayProvider({
        merchantId: import.meta.env?.VITE_UZ_MERCHANT_ID ?? 'DEMO_MERCHANT',
        serviceId: import.meta.env?.VITE_UZ_SERVICE_ID ?? 'DEMO_SERVICE'
      })
    )
  } else {
    // Россия (по умолчанию): Честный Знак, Меркурий, СБП.
    registry.register(ruCzPlugin).register(ruMercuryPlugin).register(ruSbpPlugin)
    payments.register(ruSbpPlugin)
    // Экраны модулей в админке: ядро рисует их по дескрипторам.
    moduleUi.register(czModuleUi).register(mercuryModuleUi)
  }
  await registry.initAll({ bus, db, cloud, payments })

  // Порт маркировки для ядра. В РФ его реализует модуль ЧЗ; в UZ порта нет —
  // национальную проверку «Asl Belgisi» делает uz-crpt на PRE_SALE.
  const marking: MarkingProvider | null = jurisdiction === 'RU' ? ruCzPlugin : null
  status.pluginCount = registry.size
  status.pluginIds = registry.list()

  // 5. Авто-бэкап каждые 30 минут.
  backup.start()
  status.backupScheduled = true

  // 6. Глобальный слушатель HID-сканера.
  const scanner = new ScannerService(bus)
  scanner.start()
  status.scannerListening = scanner.isListening

  // 6.1. Подключение к локальному кассовому агенту (ФЗ-54, ККТ по WebSocket).
  //      Авто-реконнект внутри сервиса; отсутствие агента не блокирует старт.
  fiscalService.connect()

  // 6.1.1. При подключении агента: статус ККТ + разовая самопроверка.
  //        Результат складываем в meta — админка покажет его как «последняя».
  fiscalService.onConnect(() => {
    fiscalService.requestStatus()
    void fiscalService
      .runSelfTest()
      .then(async (checks) => {
        await db.meta.put({ key: 'KKT_LAST_TEST', value: { at: Date.now(), checks } })
      })
      .catch(() => {
        /* ККТ может быть недоступна — не критично для старта */
      })
  })

  // 6.2. Воркер дофискализации отложенных чеков (status='offline_pending').
  //      Разгребает очередь по таймеру и сразу при подключении ККТ.
  fiscalQueueWorker.start(60000)
  // 6.2.1. Чужие offline-чеки зоны с телефонов: POS с native-ККТ — единый
  //        фискальный узел. Claim/result идёт через облачную очередь.
  cloudFiscalizationWorker.start(60000)
  fiscalService.onConnect(() => {
    void fiscalQueueWorker.processPending()
    void cloudFiscalizationWorker.processPending()
  })

  // 6.3. Электронные весы: тот же локальный агент (serial-драйвер/эмулятор).
  //      Отсутствие агента или весов не блокирует старт — касса работает
  //      с ручным вводом веса.
  scalesService.connect()

  // 7. Каталог: гарантируем активную версию (для свежей БД без миграции).
  const catalog = new CatalogService(db)
  await catalog.ensureActiveVersion()
  // Инвариант выборки по категории: у товара верхнего уровня parent = category.
  await catalog.normalizeCategoryParents()
  const assets = new AssetService(db)
  status.activeCatalogVersion = (await catalog.getActiveVersion())?.versionName ?? null

  // 8. Оркестратор продаж (PRE_SALE → PAYMENT_REQUESTED → receipts → SALE_COMPLETED).
  const sale = new SaleService(db, bus, marking)
  // 65.14: операции маркировки, прерванные падением вкладки, не должны
  // блокировать смену навсегда. Печать повторно не запускаем — только
  // возвращаем задачу в failed с сохранённым stable uuid.
  void sale
    .recoverInterruptedOperations()
    .catch((error) => console.warn('[Sale] recovery операций маркировки не удался:', error))

  // 8.1. Служба кассовых смен.
  const shift = new ShiftService(db)

  // 8.2. 65.14/9c: решения директора из облака применяются к локальному
  // hold-чеку через sale + shift. SyncService не знает про эти сервисы.
  syncService.setMarkingDecisionHandler(async (decision) => {
    const local = await db.receipts
      .where('receiptId')
      .equals(decision.receiptId)
      .first()
    if (!local) {
      return { ok: false as const, reason: 'Локальный чек не найден' }
    }
    // Идемпотентность: повторный pull после потерянного ack не должен
    // печатать/возвращать деньги второй раз. Но если локально уже принято
    // ПРОТИВОПОЛОЖНОЕ решение — это конфликт, а не успех.
    if (local.status === 'fiscalized') {
      if (decision.decision !== 'approve') {
        return { ok: false as const, reason: 'Локально чек уже фискализирован' }
      }
      // Если чек уже применён cloud-решением, но падение произошло до
      // обновления счётчиков смены — догоняем идемпотентно.
      if (local.markingDecisionSource === 'cloud' && !local.holdShiftAppliedAt) {
        const accounted = await shift.releaseHold(
          local.total,
          local.paymentMethod ?? 'cash',
          local.receiptId
        )
        if (!accounted) {
          return { ok: false as const, reason: 'Не удалось учесть hold в смене' }
        }
      }
      return { ok: true as const }
    }
    if (local.status === 'cancelled') {
      if (decision.decision !== 'cancel') {
        return { ok: false as const, reason: 'Локально чек уже отменён' }
      }
      if (local.markingDecisionSource === 'cloud' && !local.holdShiftAppliedAt) {
        const accounted = await shift.cancelHold(
          local.total,
          local.paymentMethod ?? 'cash',
          local.receiptId
        )
        if (!accounted) {
          return { ok: false as const, reason: 'Не удалось учесть hold в смене' }
        }
      }
      return { ok: true as const }
    }
    if (local.status === 'voided') {
      return { ok: false as const, reason: 'Чек уже заменён фискальным' }
    }
    if (local.status !== 'marking_review') {
      return {
        ok: false as const,
        reason: `Чек в статусе ${local.status}, решение hold неприменимо`
      }
    }

    const decidedAt =
      decision.decidedAt && Number.isFinite(Date.parse(decision.decidedAt))
        ? Date.parse(decision.decidedAt)
        : undefined
    const markApplyError = async (reason: string): Promise<void> => {
      try {
        await db.receipts.update(local.id as number, { markingApplyError: reason })
      } catch {
        /* локальная БД может быть занята — ошибка всё равно уйдёт в ack */
      }
    }
    try {
      if (decision.decision === 'approve') {
        const printed = await sale.printHeldReceipt(local.receiptId, {
          confirmedAmount: local.total,
          cashierName: decision.decidedBy ?? 'Облако',
          decisionSource: 'cloud',
          decidedBy: decision.decidedBy,
          decidedAt,
          decisionComment: decision.comment
        })
        if (!printed.ok) {
          await markApplyError(printed.reason)
          return { ok: false as const, reason: printed.reason }
        }
        const accounted = await shift.releaseHold(
          printed.receipt.total,
          printed.receipt.paymentMethod ?? 'cash',
          printed.receipt.receiptId
        )
        if (!accounted) {
          return { ok: false as const, reason: 'Не удалось учесть hold в смене' }
        }
        return { ok: true as const }
      }

      const cancelled = await sale.cancelHeldReceipt(
        local.receiptId,
        decision.comment ?? 'Решение из облака',
        decision.decidedBy ?? 'Облако',
        {
          cancelMode: decision.cancelMode ?? 'refund',
          writeoffReason: 'other',
          decisionSource: 'cloud',
          decidedBy: decision.decidedBy,
          decidedAt,
          decisionComment: decision.comment
        }
      )
      const accounted = await shift.cancelHold(
        cancelled.total,
        cancelled.paymentMethod ?? 'cash',
        cancelled.receiptId
      )
      if (!accounted) {
        return { ok: false as const, reason: 'Не удалось учесть hold в смене' }
      }
      return { ok: true as const }
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error)
      await markApplyError(reason)
      return { ok: false as const, reason }
    }
  })

  const core: AppCore = {
    bus,
    cloud,
    registry,
    backup,
    scanner,
    catalog,
    assets,
    sale,
    shift,
    marking,
    mercury: ruMercuryPlugin,
    payments,
    moduleUi,
    fiscal: fiscalService,
    scales: scalesService,
    correction: correctionService,
    status
  }

  // Единая точка восстановления всех модулей: ядро не знает их meta-ключей,
  // применяет состояние через generic-дескриптор. Серверное состояние
  // приоритетнее локального; без токена используется локальный кэш.
  await syncAllModuleStates(db, moduleUi.list(), core)
  const moduleSyncTimer = setInterval(() => {
    void syncAllModuleStates(db, moduleUi.list(), core).catch((error) => {
      console.warn('[modules] sync failed:', error instanceof Error ? error.message : error)
    })
  }, 60_000)
  moduleSyncTimer.unref?.()

  // Связь вернулась — сразу отправляем накопленные флаги модулей, не ждём
  // очередной 60-секундный тик. В Electron/Capacitor событие тоже работает.
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      void syncAllModuleStates(db, moduleUi.list(), core).catch((error) => {
        console.warn('[modules] online sync failed:', error instanceof Error ? error.message : error)
      })
    })
  }

  return core
}
